// src/firebase/useAuth.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firestore, isBotCheckAvailable } from '../firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';
import { registerUser } from './user';

// Create a context to hold the authentication state
const AuthContext = createContext();

// This is the new hook that components will use to access the auth state
export const useAuth = () => {
  return useContext(AuthContext);
};

// This is the provider component that will manage and distribute the auth state
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileInitError, setProfileInitError] = useState(false);
  const [appCheckBlocked, setAppCheckBlocked] = useState(false);
  const [dataAccessError, setDataAccessError] = useState(false);

  // Probe App Check once: initializeAppCheck succeeds even when reCAPTCHA is
  // blocked (VPN/DNS blockers, bot-like score) — only the token exchange
  // fails, and with Firestore enforcement on, every request is then denied.
  useEffect(() => {
    isBotCheckAvailable().then((available) => {
      if (!available) setAppCheckBlocked(true);
    });
  }, []);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isSubscribed: false,
    loading: true,
    plan: null,
    dueDate: null,
    isCanceled: null,
  });

  useEffect(() => {
    let unsubscribeUser = null;
    let unsubscribeSubscription = null;
    let previousEmailVerified = false;

    // This listener handles changes in user login/logout state
    const unsubscribeAuth = onAuthStateChanged(auth, async (authUser) => {
      // Clean up any existing listeners from a previous user
      if (unsubscribeUser) unsubscribeUser();
      if (unsubscribeSubscription) unsubscribeSubscription();

      if (authUser) {
        // Check if email verification status changed from false to true
        if (!previousEmailVerified && authUser.emailVerified) {
          try {
            // Force token refresh to get updated email_verified claim
            await authUser.getIdToken(true);
          } catch (error) {
            console.error('Error refreshing token:', error);
          }
        }
        previousEmailVerified = authUser.emailVerified;

        setUser(authUser);

        // If a user is logged in, set up listeners for their data
        // Listener for the main user document in 'users' collection
        const userDocRef = doc(firestore, 'users', authUser.uid);
        let recoveryInProgress = false;
        unsubscribeUser = onSnapshot(userDocRef, async (docSnapshot) => {
          if (!docSnapshot.exists() || !docSnapshot.data()?.email) {
            if (recoveryInProgress) return;
            recoveryInProgress = true;
            // Retry transient failures with backoff. On success the snapshot
            // re-fires with the created doc, so no state is set here.
            const retryDelays = [1000, 3000, 10000];
            for (let attempt = 0; attempt <= retryDelays.length; attempt += 1) {
              try {
                await registerUser(authUser.uid, authUser.email);
                break;
              } catch (error) {
                // permission-denied is deterministic (rules or App Check) —
                // retrying can't change the outcome, so fail fast.
                const permissionDenied = error.originalError?.code === 'permission-denied';
                if (permissionDenied || attempt === retryDelays.length) {
                  console.error('Failed to recover missing user document:', error);
                  // Surface the failure so the UI can show an error instead
                  // of loading forever — unless the user signed out meanwhile.
                  if (auth.currentUser?.uid === authUser.uid) {
                    setProfileInitError(true);
                  }
                  break;
                }
                await new Promise((resolve) => setTimeout(resolve, retryDelays[attempt]));
              }
            }
            recoveryInProgress = false;
            return;
          }

          setProfileInitError(false);
          setDataAccessError(false);
          const newUserData = docSnapshot.data();
          setUserData(newUserData);
          
          // Check if email_verified field changed to true
          if (newUserData?.email_verified && auth.currentUser && !auth.currentUser.emailVerified) {
            // Reload auth user and refresh token
            await auth.currentUser.reload();
            if (auth.currentUser.emailVerified) {
              await auth.currentUser.getIdToken(true);
              setUser({ ...auth.currentUser });
            }
          }
        }, (error) => {
          // Listener detaches on error and userData would stay null forever —
          // surface it so PrivateRoute can show an error screen.
          console.error('User document listener error:', error);
          if (error.code === 'permission-denied') {
            setDataAccessError(true);
          }
        });

        // Real-time listener for the subscription document.
        // This effectively caches the subscription status and keeps it live.
        const userSubscriptionRef = doc(firestore, 'users_subscriptions', authUser.uid);
        unsubscribeSubscription = onSnapshot(userSubscriptionRef, async (subSnapshot) => {
          const currentTime = new Date();
          let firestoreSubscribed = false;
          let plan = null;
          let dueDate = null;
          let isCanceled = null;

          if (subSnapshot.exists()) {
            const subData = subSnapshot.data();
            dueDate = subData.due_date?.toDate();
            plan = subData.subscription_plan;
            isCanceled = subData.subscription_canceled;
            firestoreSubscribed = dueDate && dueDate > currentTime;
          }

          // Force-refresh token to get updated custom claims
          let customClaimSubscribed = false;
          try {
            const decodedToken = await authUser.getIdTokenResult(true);
            const claimDueDate = decodedToken.claims.due_date;
            customClaimSubscribed = claimDueDate && claimDueDate > Math.floor(currentTime.getTime() / 1000);
          } catch (error) {
            console.error('Error refreshing token for custom claims:', error);
          }

          // User is subscribed if BOTH conditions are true
          const isSubscribed = firestoreSubscribed && customClaimSubscribed;

          setSubscriptionStatus({
            isSubscribed,
            plan,
            dueDate,
            isCanceled,
            loading: false,
          });
        }, (error) => {
          // Resolve as unsubscribed so consumers gated on .loading don't hang
          console.error('Subscription listener error:', error);
          setSubscriptionStatus({ isSubscribed: false, loading: false, plan: null, dueDate: null, isCanceled: null });
        });
      } else {
        // User is logged out, clear all data
        setUser(null);
        setUserData(null);
        setProfileInitError(false);
        setDataAccessError(false);
        setSubscriptionStatus({ isSubscribed: false, loading: false, plan: null, dueDate: null, isCanceled: null });
        previousEmailVerified = false;
      }
      setLoading(false); // Mark authentication as no longer loading
    });

    // Cleanup function to remove listeners when the app unmounts
    return () => {
      unsubscribeAuth();
      if (unsubscribeUser) unsubscribeUser();
      if (unsubscribeSubscription) unsubscribeSubscription();
    };
  }, []); // The empty dependency array ensures this effect runs only once

  const value = {
    user,
    userId: user?.uid,
    email: user?.email,
    emailVerified: user?.emailVerified,
    userData,
    userLoading: loading,
    profileInitError,
    appCheckBlocked,
    dataAccessError,
    subscriptionStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
